import React, { useCallback, useState } from 'react';

import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  TextInput,
  maxLength,
  minLength,
  required,
} from 'react-admin';

import stepUpActions, { StepUpAction } from './stepUpActions';

const TOTP_CODE_LENGTH = 6;
const RECOVERY_CODE_LENGTH = 16;

const stepUpCopy: Record<StepUpAction, {
  title: string;
  consequence?: string;
  authenticatorPrompt: string;
  confirmLabel: string;
}> = {
  [stepUpActions.REPLACE_DEVICE]: {
    title: 'Confirm it is you',
    authenticatorPrompt: 'Enter the code from the authenticator app you are using today. It keeps working until you have paired the new one.',
    confirmLabel: 'Continue',
  },
  [stepUpActions.REGENERATE_RECOVERY_CODES]: {
    title: 'Regenerate recovery codes',
    authenticatorPrompt: 'Enter the code from your authenticator app to confirm a new set of recovery codes. The codes you have now stop working as soon as the new ones are issued.',
    confirmLabel: 'Regenerate',
  },
  [stepUpActions.DISABLE]: {
    title: 'Turn off multi-factor authentication',
    authenticatorPrompt: 'Enter the code from your authenticator app to confirm. Your recovery codes are discarded at the same time.',
    confirmLabel: 'Turn off',
  },
  [stepUpActions.LOWER_ACCOUNT_REQUIREMENT]: {
    title: 'Stop requiring multi-factor authentication',
    consequence: 'Members who have set up multi-factor authentication can keep using it, but new and existing members are no longer required to.',
    authenticatorPrompt: 'Enter the code from your authenticator app to confirm.',
    confirmLabel: 'Stop requiring',
  },
};

export type StepUpCode = {
  totpCode?: string;
  recoveryCode?: string;
};

type StepUpFormFields = {
  totpCode?: string;
  recoveryCode?: string;
};

const MfaStepUp = ({
  loading,
  action,
  onSubmit,
  onCancel,
}: {
  loading: boolean;
  action: StepUpAction;
  onSubmit: (code: StepUpCode) => void;
  onCancel: () => void;
}) => {
  const {
    title,
    consequence,
    authenticatorPrompt,
    confirmLabel,
  } = stepUpCopy[action];
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);

  const toggleRecoveryCode = useCallback(() => {
    setUsingRecoveryCode((using) => !using);
  }, [setUsingRecoveryCode]);

  const submit = useCallback(({ totpCode, recoveryCode }: StepUpFormFields) => {
    if (usingRecoveryCode && recoveryCode) onSubmit({ recoveryCode });
    if (!usingRecoveryCode && totpCode) onSubmit({ totpCode });
  }, [onSubmit, usingRecoveryCode]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {consequence && (
        <Typography variant="body1" marginBottom={2}>
          {consequence}
        </Typography>
      )}
      <Typography variant="body1" color="text.secondary" marginBottom={2}>
        {usingRecoveryCode
          ? 'Enter one of the recovery codes you saved to authorise this change.'
          : authenticatorPrompt}
      </Typography>
      <Form onSubmit={submit} key={usingRecoveryCode ? 'recoveryCode' : 'totpCode'}>
        <Box maxWidth={260}>
          {usingRecoveryCode ? (
            <TextInput
              source="recoveryCode"
              label="Recovery code"
              disabled={loading}
              validate={[required(), minLength(RECOVERY_CODE_LENGTH)]}
              fullWidth
              autoFocus
            />
          ) : (
            <TextInput
              source="totpCode"
              label="Verification code"
              disabled={loading}
              validate={[required(), minLength(TOTP_CODE_LENGTH), maxLength(TOTP_CODE_LENGTH)]}
              fullWidth
              autoFocus
            />
          )}
        </Box>
        <Typography variant="body2" marginBottom={2}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            component="button"
            type="button"
            onClick={toggleRecoveryCode}
            disabled={loading}
          >
            {usingRecoveryCode
              ? 'Use your authenticator app instead'
              : 'Use a recovery code instead'}
          </Link>
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" type="submit" disabled={loading}>
            {confirmLabel}
          </Button>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

export default MfaStepUp;
